import gatewayService from '@/modules/common/services/gatewayService';
import httpService from '@/modules/common/services/httpService';
import logError from '@/utils/logError/logError';

const BASE = 'v3/online_presence/widget_configurations';
const CONVERSATIONS_BASE = 'v3/online_presence/ai_receptionist_conversations';
const LEARNINGS_BASE = 'v3/online_presence/ai_receptionist_learnings';
const SIMULATIONS_BASE = 'v3/online_presence/chat/simulations';
const VOICE_CALL_SETTINGS_BASE = 'v3/communication/voice_call_settings';
const AI_REC_CHANNEL_SETTINGS_BASE = 'v3/online_presence/ai_receptionist_channel_settings';
const LEGACY_ACTIVE_ENGAGE_BASE = 'business/web_presence/v2/active_engage_widget';
const VOICE_CAPABLE_SETTING_FEATURES = new Set(['VOICE', 'VOICE_SMS']);

/**
 * Normalize API response to UI shape: uid → id, business_uid → business_id; build synthetic business.
 * @param {object} data - Raw widget_configuration from API (uid, business_uid, + config fields)
 * @returns {{ config: object, business: { id: string, name: string } }}
 */
function normalizeResponse(data) {
  if (!data) {
    return { config: null, business: { id: '', name: '' } };
  }
  // Destructure top-level fields so they are not duplicated; business_name must be on config
  // so the UI shows the saved custom title after load/navigation (see WebAIWidgetConfigPanel).
  const { uid, business_uid: bizUid, business_name: bizName, ...rest } = data;
  const config = {
    id: uid,
    business_id: bizUid,
    business_name: bizName,
    ...rest,
  };
  const business = {
    id: bizUid ?? '',
    name: bizName ?? '',
  };
  return { config, business };
}

/**
 * Get main widget configuration for a business. Returns null config when none exists (404).
 * @param {string} businessUid - Business UID (from AuthStore)
 * @returns {Promise<{ config: object | null, business: { id: string, name: string } }>}
 */
async function getMainConfig(businessUid) {
  try {
    const res = await gatewayService.get(`${BASE}/main`, {
      business_uid: businessUid, // API expects snake_case
    });
    const raw = res?.data?.data?.widget_configuration ?? res?.data?.widget_configuration ?? res?.data?.data;
    return normalizeResponse(raw);
  } catch (err) {
    if (err?.response?.status === 404) {
      return {
        config: null,
        business: { id: businessUid, name: '' },
      };
    }
    throw err;
  }
}

/**
 * Upsert main widget configuration for a business.
 * @param {string} businessUid - Business UID (from AuthStore)
 * @param {object} payload - Config fields only (no id, uid, business_id); snake_case
 * @returns {Promise<{ config: object, business: { id: string, name: string } }>}
 */
async function upsertMainConfig(businessUid, payload) {
  const body = {
    business_uid: businessUid, // API expects snake_case
    ...payload,
  };
  const res = await gatewayService.put(`${BASE}/main`, body);
  const raw = res?.data?.data?.widget_configuration ?? res?.data?.widget_configuration ?? res?.data?.data;
  return normalizeResponse(raw);
}

/**
 * GET v3/online_presence/widget_configurations/public/:widget_uid — same resolved snapshot as embed/widget.
 * Used by POV style tab so empty stored fields match public JSON byte-for-byte.
 * @param {string} widgetUid - Widget configuration uid (same as config.id from main)
 * @returns {Promise<object|null>} Resolved public DTO (snake_case) or null if request fails
 */
async function getPublicResolvedConfig(widgetUid) {
  if (!widgetUid || !String(widgetUid).trim()) {
    return null;
  }
  const res = await gatewayService.get(
    `${BASE}/public/${encodeURIComponent(String(widgetUid).trim())}`,
  );
  const raw = res?.data?.data ?? res?.data;
  return raw && typeof raw === 'object' ? raw : null;
}

function parseActionsResponse(res) {
  const body = res?.data ?? {};
  let actions = [];
  if (Array.isArray(body.data)) {
    actions = body.data;
  } else if (Array.isArray(body.data?.data)) {
    actions = body.data.data;
  } else if (Array.isArray(body.data?.actions)) {
    actions = body.data.actions;
  } else if (Array.isArray(body.actions)) {
    actions = body.actions;
  } else if (Array.isArray(body)) {
    actions = body;
  }
  return actions.map((a, index) => {
    const key = String(a.name ?? a.key ?? a.type ?? '').toLowerCase() || 'action';
    const defaultLabel = a.title ?? a.label ?? a.rich_title ?? a.display_name ?? a.default_title ?? key;
    const uid = typeof a.uid === 'string' && a.uid.trim() ? a.uid.trim() : null;
    return {
      uid,
      key,
      defaultLabel,
      icon: a.icon ?? null,
      // Keep deterministic order for duplicate action types (e.g. two `schedule` actions).
      order: typeof a.position === 'number' ? a.position : index,
    };
  }).sort((a, b) => a.order - b.order);
}

/**
 * Get the list of client portal actions for this business (same as Edit portal actions dialog).
 * Tries core API first; on failure or empty, falls back to same-origin (api2) so labels always load.
 * @param {string} businessUid - Business UID (from AuthStore)
 * @returns {Promise<Array<{ uid: string|null, key: string, defaultLabel: string, icon: string|null, order: number }>>}
 */
async function getAvailablePortalActions(businessUid) {
  if (!businessUid) return [];
  const ACTIONS_PATH = 'business/web_presence/v2/actions/';
  const params = { context: 'client_portal', new_api: true, teams_view_filter: '{}' };
  try {
    const res = await gatewayService.get(ACTIONS_PATH, params);
    const list = parseActionsResponse(res);
    if (list.length > 0) return list;
  } catch (err) {
    logError('Failed to fetch portal actions via gateway', err, { businessUid });
  }
  try {
    const res = await httpService.get(ACTIONS_PATH, params);
    return parseActionsResponse(res);
  } catch (err) {
    logError('Failed to fetch portal actions via httpService fallback', err, { businessUid });
    return [];
  }
}

/**
 * List conversations for the logged-in business (backend derives business from auth; do not pass business_uid).
 * @param {object} params - { page?: number, per_page?: number, query?: string, include_simulated?: boolean }
 * @returns {Promise<{ conversations: Array<object>, total: number }>}
 */
async function getConversations(params = {}) {
  const {
    page = 1,
    per_page: perPage = 25,
    query,
    sort = 'started_at:desc',
    include_simulated: includeSimulated = false,
  } = params;
  const requestParams = { page, per_page: perPage, sort, include_simulated: includeSimulated === true };
  if (query != null && String(query).trim() !== '') {
    requestParams.q = String(query).trim();
  }
  const res = await gatewayService.get(CONVERSATIONS_BASE, requestParams);
  const data = res?.data?.data ?? res?.data;
  const conversations = Array.isArray(data?.conversations) ? data.conversations : [];
  const total = typeof data?.total === 'number' ? data.total : 0;
  return { conversations, total };
}

/**
 * Get a single conversation by uid (for transcript modal). Backend scopes to logged-in business.
 * @param {string} uid - Conversation UID
 * @returns {Promise<object>} Conversation object (uid, transcript, summary, etc.)
 */
async function getConversation(uid) {
  const res = await gatewayService.get(`${CONVERSATIONS_BASE}/${uid}`);
  const conversation = res?.data?.data ?? res?.data;
  return conversation ?? null;
}

function normalizeVoiceCallSettingsResponse(res) {
  const body = res?.data ?? res ?? {};
  const candidates = [
    body?.data?.data,
    body?.data,
    body,
  ].filter(Boolean);
  const isVoiceCapableSetting = (setting) => (
    !setting?.feature || VOICE_CAPABLE_SETTING_FEATURES.has(setting.feature)
  );
  const data = candidates.find((candidate) => Array.isArray(candidate?.voice_call_settings))
    ?? candidates.find((candidate) => candidate?.voice_call_setting)
    ?? candidates.find((candidate) => candidate?.uid)
    ?? {};
  if (Array.isArray(data.voice_call_settings)) {
    return data.voice_call_settings.filter(isVoiceCapableSetting);
  }
  if (data.voice_call_setting) {
    return isVoiceCapableSetting(data.voice_call_setting) ? [data.voice_call_setting] : [];
  }
  return data && typeof data === 'object' && data.uid && isVoiceCapableSetting(data) ? [data] : [];
}

function normalizeAiRecChannelSettingsResponse(res) {
  const data = res?.data?.data ?? res?.data ?? {};
  const setting = data?.ai_receptionist_channel_setting
    ?? data?.data?.ai_receptionist_channel_setting
    ?? data;
  return setting && typeof setting === 'object' ? setting : null;
}

function normalizeAiRecChannelVoicesResponse(res) {
  const data = res?.data?.data ?? res?.data ?? {};
  if (Array.isArray(data?.voices)) return data.voices;
  if (Array.isArray(data?.data?.voices)) return data.data.voices;
  if (Array.isArray(data)) return data;
  return [];
}

async function getVoiceCallSettings() {
  const res = await gatewayService.get(VOICE_CALL_SETTINGS_BASE);
  return normalizeVoiceCallSettingsResponse(res);
}

async function updateVoiceCallSettings(settingsUid, payload) {
  const res = await gatewayService.put(`${VOICE_CALL_SETTINGS_BASE}/${settingsUid}`, payload);
  return normalizeVoiceCallSettingsResponse(res)[0] || null;
}

async function getVoiceChannelSettings(channelUid) {
  const safeChannelUid = encodeURIComponent(String(channelUid || '').trim());
  if (!safeChannelUid) return null;
  const res = await gatewayService.get(`${AI_REC_CHANNEL_SETTINGS_BASE}/${safeChannelUid}`);
  return normalizeAiRecChannelSettingsResponse(res);
}

async function getVoiceChannelVoices() {
  const res = await gatewayService.get(`${AI_REC_CHANNEL_SETTINGS_BASE}/voices`);
  return normalizeAiRecChannelVoicesResponse(res);
}

async function previewVoiceChannelWelcomeMessage(settings) {
  const res = await gatewayService.post(`${AI_REC_CHANNEL_SETTINGS_BASE}/voice_preview`, {
    settings,
  });
  const data = res?.data?.data ?? res?.data ?? {};
  return data?.voice_preview ?? data?.data?.voice_preview ?? null;
}

async function createVoiceChannelSettings(channelUid, settings) {
  const res = await gatewayService.post(AI_REC_CHANNEL_SETTINGS_BASE, {
    channel_uid: channelUid,
    settings,
  });
  return normalizeAiRecChannelSettingsResponse(res);
}

async function updateVoiceChannelSettings(channelUid, settings) {
  const safeChannelUid = encodeURIComponent(String(channelUid || '').trim());
  const res = await gatewayService.put(`${AI_REC_CHANNEL_SETTINGS_BASE}/${safeChannelUid}`, {
    settings,
  });
  return normalizeAiRecChannelSettingsResponse(res);
}

async function deleteVoiceChannelSettings(channelUid) {
  const safeChannelUid = encodeURIComponent(String(channelUid || '').trim());
  if (!safeChannelUid) return;
  await gatewayService.delete(`${AI_REC_CHANNEL_SETTINGS_BASE}/${safeChannelUid}`);
}

/**
 * Patch a conversation by uid (for transcript-level feedback persistence).
 * Backend scopes to logged-in business.
 * @param {string} uid - Conversation UID
 * @param {object} payload - Partial conversation payload (e.g. transcript)
 * @returns {Promise<object>} Updated conversation object
 */
async function updateConversation(uid, payload) {
  const res = await gatewayService.patch(`${CONVERSATIONS_BASE}/${uid}`, payload || {});
  const conversation = res?.data?.data ?? res?.data;
  return conversation ?? null;
}

/** Learning statuses the conversation view renders, each with its own UI state. */
const VISIBLE_LEARNING_STATUSES = new Set([
  'open', // awaiting the owner's answer
  'answered', // answer sent to the BizAI training chat, nothing applied yet
  'applied', // the training workflow wrote a real change
]);

/**
 * List AI-generated learning opportunities for a conversation. `applied` records
 * are included so the owner gets positive confirmation that training landed —
 * only `dismissed` is hidden. Backend scopes to the logged-in business.
 * @param {string} conversationUid - Conversation UID
 * @returns {Promise<Array<object>>} Learning records (uid, question, status, target_type, ...)
 */
async function getConversationLearnings(conversationUid) {
  const res = await gatewayService.get(LEARNINGS_BASE, {
    conversation_uid: conversationUid,
    source: 'ai_opportunity',
    per_page: 100,
  });
  const data = res?.data?.data ?? res?.data;
  const learnings = Array.isArray(data?.learnings) ? data.learnings : [];
  return learnings.filter((l) => VISIBLE_LEARNING_STATUSES.has(l?.status));
}

/**
 * Create a learning record. Used by the "Improve answer" flow to persist a
 * user-initiated correction (source: 'user_improve_answer').
 * Backend scopes to the logged-in business.
 * @param {object} payload - { conversation_uid, source, target_type, message_index?,
 *   message_snippet?, answer_text? }
 * @returns {Promise<object>} Created learning object
 */
async function createLearning(payload) {
  const res = await gatewayService.post(LEARNINGS_BASE, payload || {});
  const learning = res?.data?.data ?? res?.data;
  return learning ?? null;
}

/**
 * Patch a learning record by uid (record the owner answer / mark applied / dismiss).
 * Backend scopes to the logged-in business.
 * @param {string} uid - Learning UID
 * @param {object} payload - Partial learning payload (status, answer_text, bizai_conversation_uid, ...)
 * @returns {Promise<object>} Updated learning object
 */
async function updateLearning(uid, payload) {
  const res = await gatewayService.patch(`${LEARNINGS_BASE}/${uid}`, payload || {});
  const learning = res?.data?.data ?? res?.data;
  return learning ?? null;
}

/**
 * Dismiss a learning opportunity (soft-delete), so it stops being offered.
 * Only meaningful before an answer was sent — once training has the answer there
 * is nothing to decline. Backend scopes to the logged-in business.
 * @param {string} uid - Learning UID
 * @returns {Promise<object>} The dismissed learning object
 */
async function dismissLearning(uid) {
  const res = await gatewayService.remove(`${LEARNINGS_BASE}/${uid}`);
  const learning = res?.data?.data ?? res?.data;
  return learning ?? null;
}

/**
 * Start (or attach to active) chat simulation for current staff business context.
 * @param {{ scenario: string, page_url?: string }} payload
 * @returns {Promise<{
 *   simulation_id: string,
 *   status: string,
 *   conversation_uid?: string|null,
 *   conversation_title?: string|null,
 *   created_new?: boolean,
 * }>}
 */
async function startChatSimulation(payload) {
  const body = {
    scenario: String(payload?.scenario || '').trim(),
  };
  if (payload?.page_url) body.page_url = String(payload.page_url).trim();
  if (payload?.welcome_message) body.welcome_message = String(payload.welcome_message).trim();
  if (Array.isArray(payload?.welcome_buttons) && payload.welcome_buttons.length > 0) {
    body.welcome_buttons = payload.welcome_buttons;
  }

  const res = await gatewayService.post(SIMULATIONS_BASE, body);
  const data = res?.data?.data ?? res?.data ?? {};
  return {
    simulation_id: data?.simulation_id || '',
    status: data?.status || '',
    conversation_uid: data?.conversation_uid ?? null,
    conversation_title: data?.conversation_title ?? null,
    created_new: data?.created_new,
    error_code: data?.error_code ?? null,
    error_message: data?.error_message ?? null,
    started_at: data?.started_at ?? null,
    ended_at: data?.ended_at ?? null,
  };
}

/**
 * Poll simulation status for current staff business context.
 * @param {string} simulationId
 * @returns {Promise<{
 *   simulation_id: string,
 *   status: string,
 *   conversation_uid?: string|null,
 *   conversation_title?: string|null,
 *   error_code?: string|null,
 *   error_message?: string|null,
 *   started_at?: string|null,
 *   ended_at?: string|null,
 * }>}
 */
async function getChatSimulationStatus(simulationId) {
  const safeId = String(simulationId || '').trim();
  if (!safeId) return null;
  const res = await gatewayService.get(`${SIMULATIONS_BASE}/${encodeURIComponent(safeId)}`);
  const data = res?.data?.data ?? res?.data ?? {};
  return {
    simulation_id: data?.simulation_id || safeId,
    status: data?.status || '',
    conversation_uid: data?.conversation_uid ?? null,
    conversation_title: data?.conversation_title ?? null,
    error_code: data?.error_code ?? null,
    error_message: data?.error_message ?? null,
    started_at: data?.started_at ?? null,
    ended_at: data?.ended_at ?? null,
    // Live transcript-so-far (visitor projection); renders in the Training tab while running.
    messages: Array.isArray(data?.messages) ? data.messages : null,
  };
}

/**
 * GET v2/weekly_availabilities/:uid (general availability) via APIGW. Used for default business hours.
 * @param {string} weeklyAvailabilityUid - weekly_availability_uid from owner staff
 * @returns {Promise<{ time_slots_by_day: Array }>} Response with time_slots_by_day
 */
async function getWeeklyAvailabilityGeneral(weeklyAvailabilityUid) {
  if (!weeklyAvailabilityUid || !weeklyAvailabilityUid.trim()) {
    return null;
  }
  const res = await httpService.get(`/v2/weekly_availabilities/${weeklyAvailabilityUid.trim()}`, {
    general_availability: 'true',
    use_selected_staff_time_zone: 'true',
  });
  const data = res?.data ?? res;
  return data ?? null;
}

/**
 * Get legacy Active Engage widget settings needed for migration toggle state.
 * Authenticated same-origin endpoint.
 * @returns {Promise<{ sub_widget_type: string|null }>}
 */
async function getLegacyActiveEngageSettings() {
  const res = await gatewayService.get(LEGACY_ACTIVE_ENGAGE_BASE);
  const data = res?.data?.data ?? res?.data ?? {};
  return {
    sub_widget_type: typeof data?.sub_widget_type === 'string' ? data.sub_widget_type : null,
  };
}

/**
 * Persist legacy Active Engage migration payload in sub_widget_type.
 * @param {string|null} subWidgetType
 * @returns {Promise<void>}
 */
async function updateLegacyActiveEngageSettings(subWidgetType) {
  await gatewayService.put(LEGACY_ACTIVE_ENGAGE_BASE, {
    sub_widget_type: subWidgetType,
  });
}

export default {
  getMainConfig,
  upsertMainConfig,
  getPublicResolvedConfig,
  getAvailablePortalActions,
  getConversations,
  getConversation,
  updateConversation,
  getConversationLearnings,
  createLearning,
  updateLearning,
  dismissLearning,
  startChatSimulation,
  getChatSimulationStatus,
  getVoiceCallSettings,
  updateVoiceCallSettings,
  getVoiceChannelSettings,
  getVoiceChannelVoices,
  previewVoiceChannelWelcomeMessage,
  createVoiceChannelSettings,
  updateVoiceChannelSettings,
  deleteVoiceChannelSettings,
  getWeeklyAvailabilityGeneral,
  getLegacyActiveEngageSettings,
  updateLegacyActiveEngageSettings,
};
